import type { Filter, FilterMatchMode } from "@/shared/components/ResourceView";

/**
 * ``EQUALS``-only comparator allow-list for the feedback table's opaque-id
 * ``string`` filter (Session / ``thread_id``): the feedback GET treats the id
 * as exact equality and has no exclusion surface, so the OperatorPicker is
 * narrowed to ``EQUALS``.
 *
 * The boolean pseudo-fields and the feedback enums need no allow-list — in the
 * filter model a non-``nullable`` boolean is already ``EQUALS``-only, and an
 * enum already offers ``EQUALS`` / ``NOT_EQUALS`` (the read-side mapper drops
 * the unsupported ``NOT_EQUALS``).
 */
export const EQUALS_ONLY = ["EQUALS"] as const;

/**
 * Fields the admin thread search exposes as boolean pseudo-fields. They are
 * not columns on ``agent_sessions``; the backend extracts them out of the
 * condition tree and renders bespoke predicates, and only accepts them at the
 * top level of an AND group (see ``extract_boolean_pseudo_field_predicates``
 * in ``agent_thread_repo.py``).
 */
const PSEUDO_FIELDS: ReadonlySet<string> = new Set([
  "has_errors",
  "is_customer",
]);

/** True when any active filter targets an admin boolean pseudo-field. */
export function hasPseudoFieldFilter(filters: Filter[]): boolean {
  return filters.some((f) => PSEUDO_FIELDS.has(f.field));
}

/**
 * The match mode to submit with the admin thread search condition.
 *
 * An OR match mode wraps every user filter in an OR group, but the backend
 * rejects a pseudo-field (`has_errors` / `is_customer`) inside OR/NOT with a
 * 400. So coerce to `AND` whenever a pseudo-field filter is present; otherwise
 * honor the user's choice. Forcing AND keeps the pseudo-field at the top
 * level, where the backend accepts it.
 */
export function submittedMatchMode(
  filters: Filter[],
  userMatchMode: FilterMatchMode,
): FilterMatchMode {
  return hasPseudoFieldFilter(filters) ? "AND" : userMatchMode;
}
